
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  key_skills: string[];
  work_presence: string;
  work_arrangement: string;
  salary_range_min?: number;
  salary_range_max?: number;
  salary_currency: string;
  industry: string;
  custom_industry?: string;
  seniority?: string;
  created_at: string;
  status: string;
  is_active: boolean;
  key_responsibilities?: string;
  years_of_experience?: string;
  applicant_count: number;
  organization: {
    organization?: string;
    full_name?: string;
    id: string;
    logo_url?: string;
  };
  creator?: {
    id: string;
    organization?: string;
    full_name?: string;
  };
}

export function useJob(jobId: string | undefined) {
  return useQuery({
    queryKey: ['test', jobId],
    queryFn: async () => {
      if (!jobId) return null;

      // First query to get test details
      const { data, error } = await supabase
        .from('tests')
        .select(`
          id,
          title,
          description,
          location,
          key_skills,
          work_presence,
          work_arrangement,
          key_responsibilities,
          years_of_experience,
          seniority,
          salary_range_min,
          salary_range_max,
          salary_currency,
          industry,
          custom_industry,
          created_at,
          status,
          is_active,
          creator_id
        `)
        .eq('id', jobId)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) return null;

      // Second query to get applicant count
      const { count: applicantCount, error: countError } = await supabase
        .from('test_responses')
        .select('id', { count: 'exact' })
        .eq('test_id', jobId)
        .eq('status', 'completed');

      if (countError) {
        console.error('Error fetching applicant count:', countError);
      }

      // Third query to get secure employer info using the secure function
      const { data: employerInfo, error: employerError } = await supabase
        .rpc('get_public_employer_info', { employer_id: data.creator_id });

      if (employerError) {
        console.error('Error fetching employer info:', employerError);
      }

      const employer = employerInfo?.[0];
      
      return {
        ...data,
        status: data.status || 'draft',
        is_active: data.is_active !== false,
        salary_currency: data.salary_currency || 'INR',
        applicant_count: applicantCount || 0,
        organization: {
          id: employer?.id || data.creator_id,
          organization: employer?.company_name,
          logo_url: employer?.company_logo_url
        }
      } as Job;
    },
    enabled: !!jobId,
  });
}
